import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Modal, Input, Checkbox, Empty, Spin } from 'antd'
import { useAppSelector } from '../../../app/hooks'
import { fetchManageData } from '../../photoTag/api'
import type { PhotoTag } from '../../photoTag/types'
import './index.scss'

const { Search } = Input

export interface TagPickerProps {
  visible: boolean
  checkedList: string[]
  // 保留 matchAll / hideMatchLogic prop 以兼容现有调用方；当前一张照片只允许 1 个标签，
  // AND 筛选永远空，UI 已下线，matchAll 始终按 false 透传给 onOK。
  matchAll: boolean
  onCancel: () => void
  onOK: (
    checkedList: string[],
    matchAll: boolean,
    checkedNames?: string[]
  ) => void
  hideMatchLogic?: boolean
  title?: string
  okText?: string
  fromPlace?:
    | '全部照片筛选标签页'
    | '全部照片批量添加照片页'
    | '全部照片批量移除照片页'
  /** 单选模式（一张照片只能 1 个标签的产品约束下用）— 选中新的会自动取消之前的 */
  singleSelect?: boolean
  /** 用户在弹窗内首次输入搜索词时回调 (一次搜索会话只触发一次), 调用方上报 dialog_click(search) */
  onSearch?: () => void
}

export const TagPicker: React.FC<TagPickerProps> = ({
  visible,
  checkedList,
  onCancel,
  onOK,
  title,
  okText,
  singleSelect,
  onSearch
}) => {
  const groupID = useAppSelector((state) => state.group.groupId)
  const userID = useAppSelector((state) => state.main.user?.user_id)

  const [loading, setLoading] = useState(false)
  const [teamTags, setTeamTags] = useState<PhotoTag[]>([])

  const [internalCheckedList, setInternalCheckedList] = useState<string[]>([])
  const checkedListRef = useRef<string[]>([])
  checkedListRef.current = internalCheckedList

  const [teamSearchValue, setTeamSearchValue] = useState('')
  // 一次搜索会话 (空→非空) 只上报一次 search, 清空后允许下次再报
  const searchTrackedRef = useRef(false)

  const prevVisibleRef = useRef(false)

  const loadTags = useCallback(() => {
    if (!groupID || !userID) return
    setLoading(true)
    fetchManageData(groupID, userID)
      .then((data) => {
        const tGroups = data.team?.groups || []
        // 过滤 disabled 标签：跟单照片路径（useTeamPhotoTags hook 内部已过滤）保持一致。
        // fetchManageData 走 H5PhotoLabelManage 接口，后端返全量含 disabled（因为
        // 「管理团队标签」面板需要展示+恢复停用标签）。但「批量添加标签」打标场景
        // 应该跟单照片打标一致——只展示 enabled。
        const tTags = tGroups
          .flatMap((g) => g.labels || [])
          .filter((l: any) => !l.disabled)

        setTeamTags(
          tTags.map((l) => ({
            id: String(l.id),
            name: l.name,
            color: l.color,
            scope: 'team'
          }))
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [groupID, userID])

  useEffect(() => {
    const justOpened = visible && !prevVisibleRef.current
    prevVisibleRef.current = visible

    if (justOpened) {
      setInternalCheckedList(checkedList || [])
      setTeamSearchValue('')
      searchTrackedRef.current = false
      loadTags()
    }
  }, [visible, checkedList, loadTags])

  const filteredTeamTags = useMemo(() => {
    return teamTags.filter((t) => t.name.includes(teamSearchValue))
  }, [teamTags, teamSearchValue])

  const handleCheck = useCallback(
    (id: string, checked: boolean) => {
      setInternalCheckedList((prev) => {
        if (singleSelect) {
          // 单选：选中 = 只剩这一个；取消 = 清空
          return checked ? [id] : []
        }
        if (checked) {
          return prev.includes(id) ? prev : [...prev, id]
        }
        return prev.filter((item) => item !== id)
      })
    },
    [singleSelect]
  )

  const renderTags = (tags: PhotoTag[]) => {
    if (tags.length === 0)
      return <Empty description="暂无标签" style={{ marginTop: 40 }} />
    return (
      <div className="tag-picker-list">
        {tags.map((tag) => {
          const isChecked = internalCheckedList.includes(tag.id)
          return (
            <div
              key={tag.id}
              className={`tag-picker-item ${isChecked ? 'active' : ''}`}
              onClick={() => handleCheck(tag.id, !isChecked)}
            >
              <Checkbox checked={isChecked} style={{ marginRight: 8 }} />
              <span
                className="tag-color-dot"
                style={{ backgroundColor: tag.color || '#2979FF' }}
              />
              <span className="tag-name">{tag.name}</span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Modal
      title={title || '筛选照片标签'}
      width={680}
      open={visible}
      onCancel={onCancel}
      onOk={() => {
        const checkedNames = checkedListRef.current
          .map((id) => teamTags.find((t) => t.id === id)?.name)
          .filter((name): name is string => !!name)
        onOK(checkedListRef.current, false, checkedNames)
      }}
      okText={okText}
      className="tag-picker-modal"
      destroyOnHidden
    >
      <Spin spinning={loading}>
        <div style={{ padding: '16px' }}>
          <Search
            placeholder="搜索标签名称"
            value={teamSearchValue}
            onChange={(e) => {
              const v = e.target.value
              setTeamSearchValue(v)
              if (v && !searchTrackedRef.current) {
                searchTrackedRef.current = true
                onSearch && onSearch()
              } else if (!v) {
                searchTrackedRef.current = false
              }
            }}
            style={{ width: 280, marginBottom: 16 }}
          />
          {renderTags(filteredTeamTags)}
        </div>
      </Spin>
    </Modal>
  )
}

export default TagPicker
